/**
 * HomeHero -- the Observatory night-sky hero (P214.4).
 *
 * Mounted into AppShell's `heroSlot` (full-bleed navy, flush under the
 * header, Home route only). Lives in twin/ because it reads the cascade
 * (twin) for the node count alongside the board snapshot (shared) for the
 * gauge / posture / SAR -- twin may import shared. No new state; pure
 * render of existing hooks.
 *
 * Layout: a 1.5fr/1fr grid -- left = kicker + serif H1 + lede; right = a
 * 240px posture gauge dial. A 4-cell horizon stat strip sits beneath.
 * Decorative star + constellation layers are aria-hidden / pointer-none.
 */
import { useEffect } from "react";
import { localeFor } from "shared/time/formatDate";
import { InfoTip } from "shared/components/Explainer";
import { useTranslation } from "react-i18next";

import { derivePosture, type PostureTone } from "twin/home/heroPosture";
import { useBoardSnapshot } from "shared/board/useBoardSnapshot";
import { formatSARCompact, isMoneyNotMeasured } from "shared/money/money";
import { canReadBoard } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { useCascade } from "twin/api/hooks";
import { usePosture } from "shared/posture/usePosture";

import { ownsSomething } from "shared/api/authority";
const CIRC = 2 * Math.PI * 104; // gauge arc circumference (r=104)

// Render the board's ISO `as_of` as a compact, readable UTC stamp
// ("10 Jun 2026, 09:00 UTC") instead of the raw machine timestamp.
// Falls back to the raw string if it isn't a parseable date.
// Round 16 I14 -- in the reader's locale, not pinned to en-GB.
function formatStamp(iso: string, language: string | undefined): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${new Intl.DateTimeFormat(localeFor(language), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(d)} UTC`;
}

const POSTURE: Record<
  PostureTone,
  { kicker: string; arc: string; num: string; kick: string }
> = {
  red: {
    kicker: "home.hero.kickerDegraded",
    arc: "stroke-rag-red",
    num: "text-ragNight-red",
    kick: "text-ragNight-redKick",
  },
  amber: {
    kicker: "home.hero.kickerElevated",
    arc: "stroke-rag-amber",
    num: "text-ragNight-amber",
    kick: "text-ragNight-amber",
  },
  green: {
    kicker: "home.hero.kickerNormal",
    arc: "stroke-rag-green",
    num: "text-ragNight-green",
    kick: "text-ragNight-green",
  },
  // Not a status. The board snapshot is an approver artefact, so for
  // everyone else this hero had no data -- and rendered the absence as
  // GREEN. It told the Resilience Lead her bank was fine because she
  // was not allowed to be told otherwise, which is the failure this
  // product exists to remove, on its own front page.
  unknown: {
    kicker: "home.hero.kickerUnknown",
    arc: "stroke-slate-500",
    num: "text-slate-300",
    kick: "text-slate-400",
  },
  // Round 12 B1 -- also not a status, and a different one. `unknown`
  // means the hero could not READ the posture; this means it read it,
  // and the answer is that nothing has been assessed. Same slate,
  // because neither is a colour the estate has earned.
  not_assessed: {
    kicker: "home.hero.kickerNotAssessed",
    arc: "stroke-slate-500",
    num: "text-slate-300",
    kick: "text-slate-400",
  },
};

function StarsField() {
  // 12 faint stars, deterministic positions, full-bleed.
  const stars = [
    [6, 22, 1.2],
    [14, 60, 1],
    [22, 38, 1.4],
    [31, 74, 1],
    [38, 18, 1.1],
    [47, 52, 1.3],
    [58, 28, 1],
    [66, 68, 1.2],
    [74, 40, 1],
    [82, 20, 1.3],
    [89, 58, 1.1],
    [95, 36, 1],
  ];
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-50"
      preserveAspectRatio="none"
      viewBox="0 0 100 100"
    >
      {stars.map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} className="fill-sky-dim" />
      ))}
    </svg>
  );
}

function Constellation() {
  // The brand glyph, large + faint, anchored to the trailing edge.
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      className="pointer-events-none absolute top-[30px] w-[560px] opacity-50 text-brand-brass"
      style={{ insetInlineEnd: "-40px" }}
    >
      <polyline
        points="8,85 22,30 40,55 55,12 70,55 85,30 95,85"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="55" cy="12" r="3" fill="#D9BC6C" />
    </svg>
  );
}

function PostureGauge({
  impacted,
  total,
  rag,
  sar,
}: {
  impacted: number;
  total: number;
  rag: PostureTone;
  sar: string;
}) {
  const { t } = useTranslation();
  const tone = POSTURE[rag];
  const frac = total > 0 ? impacted / total : 0;
  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[240px] w-[240px]">
        <svg viewBox="0 0 240 240" className="h-full w-full -rotate-90">
          <circle
            cx="120"
            cy="120"
            r="104"
            fill="none"
            strokeWidth="10"
            className="stroke-brand-navyLine"
          />
          <circle
            cx="120"
            cy="120"
            r="104"
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            className={tone.arc}
            strokeDasharray={`${frac * CIRC} ${CIRC}`}
          />
          <circle
            cx="120"
            cy="120"
            r="88"
            fill="none"
            strokeWidth="1"
            strokeDasharray="2 5"
            className="stroke-brand-navyLine"
          />
        </svg>
        {/* Upright overlay centred on the (rotated) dial. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* D2 -- the pair is atomic. Isolating each number on its own
              still lets right-to-left swap which one the reader meets
              first, so 11 of 12 was read back as 12 of 11. */}
          <div className="flex items-baseline" dir="ltr">
            {/* An em dash, not a zero: "0 of 0 impacted" is a claim. */}
            <span className={`font-display text-[52px] font-semibold ${tone.num}`}>
              {rag === "unknown" ? "\u2014" : impacted}
            </span>
            {rag !== "unknown" && total > 0 && (
              <span className="font-display text-2xl text-sky-dim">/{total}</span>
            )}
          </div>
          <span className="font-mono text-[9.5px] uppercase tracking-[.18em] text-sky-label">
            {t("home.hero.gaugeImpacted")}
            {/* F2: "8 / 12 IBSES IMPACTED" is the first thing four of the
                six personas ever saw, and none of them knew what an IBS
                was. The gauge has no room for a sentence, so the tip
                carries it. */}
            <InfoTip termKey="ibs" tone="dark" marker="i" />
          </span>
        </div>
      </div>
      <div className="mt-[18px] flex flex-col items-center">
        {/* A figure we do not have is not a figure. The sentinel used to
            print verbatim -- "__not_measured__ at risk". */}
        {!isMoneyNotMeasured(sar) && (
          <span className="font-display text-2xl font-semibold text-brand-cream">
            {sar}{" "}
            <span className="font-mono text-sm text-sky-label">
              {t("home.hero.gaugeAtRisk")}
            </span>
          </span>
        )}
        <span
          className={`mt-1 font-mono text-[10px] uppercase tracking-[.12em] ${tone.kick}`}
        >
          {t("home.hero.gaugeRated", { rag: rag.toUpperCase() })}
        </span>
      </div>
    </div>
  );
}

function HorizonStrip({
  entities,
  nodes,
  mvbImpacted,
  mvbTotal,
  sev,
}: {
  entities: number;
  nodes: number;
  mvbImpacted: number;
  mvbTotal: number;
  sev: Record<string, number>;
}) {
  const { t } = useTranslation();
  const mvbFull = mvbTotal > 0 && mvbImpacted >= mvbTotal;
  const cells = [
    { label: t("home.hero.horizonEntities"), value: <>{entities}</> },
    { label: t("home.hero.horizonNodes"), value: <>{nodes}</> },
    {
      label: t("home.hero.horizonMvb"),
      value: (
        <span className={mvbFull ? "text-ragNight-red" : undefined}>
          {mvbImpacted}{" "}
          <span className="font-mono text-sm text-sky-label">/ {mvbTotal}</span>
        </span>
      ),
    },
    {
      label: t("home.hero.horizonResilience"),
      value: (
        <span className="inline-flex items-baseline gap-2">
          <span className="text-ragNight-red">{sev.critical ?? sev.red ?? 0}</span>
          <span className="text-ragNight-amber">{sev.high ?? sev.amber ?? 0}</span>
          <span className="text-ragNight-green">{sev.low ?? sev.green ?? 0}</span>
        </span>
      ),
    },
  ];
  return (
    <div className="relative border-t border-brand-navyLine bg-brand-navy2/55">
      <div className="grid grid-cols-2 lg:grid-cols-4">
        {cells.map((c, i) => (
          <div
            key={i}
            className={`px-6 py-[18px] ${i > 0 ? "border-s border-brand-navyLine" : ""}`}
          >
            <div className="font-mono text-[9.5px] uppercase tracking-[.16em] text-sky-label">
              {c.label}
            </div>
            <div className="mt-[7px] font-display text-2xl font-semibold text-brand-cream">
              {c.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Round 20 U9 -- how often the hero asks for "now" again. */
export const HERO_REFRESH_MS = 30_000;

export function HomeHero() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  // D8 -- not above the owner's own work.
  //
  // The hero is the estate-wide posture: a board and cascade rollup for
  // somebody whose question is "how is the bank". The Service Owner's
  // question is "what is asked of me", and his home is a work list --
  // measured at y 58 to 624 of a 900px fold with his panel at 662, so
  // the answer he came for was below it. Fourth round.
  //
  // Skipped rather than shrunk: a smaller hero would still be the wrong
  // page's answer occupying the top of his.
  const ownerHome = ownsSomething(me.data) && !me.data?.is_admin;
  // Don't ask for what will be refused: the board is an approver
  // artefact, so for everyone else this is a guaranteed 403 on every
  // Home load -- and the hero renders the honest unknown state instead.
  const board = useBoardSnapshot({ enabled: canReadBoard(me.data) });
  const cascade = useCascade();
  // Round 20 U9 -- home read "as of 14:44" at 14:47:37: the page kept the
  // answer it loaded and never asked again. Asked again every half
  // minute, the stamp stays within the minute of the read.
  const { refetch: refetchBoard } = board;
  const { refetch: refetchCascade } = cascade;
  const boardOn = canReadBoard(me.data);
  useEffect(() => {
    const id = window.setInterval(() => {
      if (boardOn) void refetchBoard();
      void refetchCascade();
    }, HERO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [boardOn, refetchBoard, refetchCascade]);
  // Board first, cascade second, silence third -- never a default that
  // reads as good news (B1).
  // Stitching review 5.4 -- the counts are the posture's, for every seat.
  const postureFigures = usePosture();
  const posture = derivePosture(board.data, cascade.data, postureFigures.data);
  const { rag, impacted, total } = posture;
  const sar = formatSARCompact(posture.revenueAtRisk);
  const asOf = board.data?.as_of ?? cascade.data?.as_of;
  const nodes = cascade.data?.cascade_node_count ?? 0;
  const sev = cascade.data?.impacted_by_severity ?? {};
  const degraded = impacted > 0;
  const tone = POSTURE[rag];

  if (ownerHome) return null;

  return (
    <div className="relative mx-auto max-w-[1260px] px-10">
      <StarsField />
      <Constellation />
      <div className="relative grid grid-cols-1 items-center gap-[60px] pb-16 pt-[58px] lg:grid-cols-[1.5fr_1fr]">
        <div>
          {/* D3 -- the kicker row could not wrap, so a label a third
              longer in another language had nowhere to go. */}
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span
              aria-hidden="true"
              className={`h-[9px] w-[9px] rounded-full ${
                rag === "red"
                  ? "bg-rag-red"
                  : rag === "amber"
                    ? "bg-rag-amber"
                    : "bg-rag-green"
              } shadow-[0_0_12px] ${
                rag === "red"
                  ? "shadow-rag-red"
                  : rag === "amber"
                    ? "shadow-rag-amber"
                    : "shadow-rag-green"
              } animate-pulse motion-reduce:animate-none`}
            />
            <span
              className={`font-mono text-[11px] uppercase tracking-[.2em] ${tone.kick}`}
            >
              {t(tone.kicker)}
            </span>
            {asOf ? (
              <span className="ms-auto font-mono text-[11px] text-sky-dim">
                {t("home.hero.asOf", { stamp: formatStamp(asOf, i18n.language) })}
              </span>
            ) : null}
          </div>
          <h1 className="max-w-[18ch] font-display text-[54px] font-medium leading-[1.08] tracking-[-.01em] text-brand-cream">
            {degraded ? (
              <>
                {/* Count leads as the coloured serif figure; the rest of
                    the sentence reads in cream. Honest + live -- no
                    fabricated time-of-day. */}
                <b className={`font-semibold ${tone.num}`}>
                  {t("home.hero.headImpacted", { count: impacted })}
                </b>{" "}
                {t("home.hero.headTail")}
              </>
            ) : (
              t("home.hero.headSteady")
            )}
          </h1>
          <p className="mt-5 max-w-[50ch] text-[15px] leading-[1.65] text-sky-muted">
            <b className="font-medium text-brand-cream">{t("home.hero.ledeLead")}</b>{" "}
            {t("home.hero.ledeRest")}
          </p>
          {/* Say where the number came from: the same estate the board
              snapshot reads, which is why the card below agrees with
              it. D10 -- this used to add "the board view is the
              approver's", which told a reviewer a page was not hers
              after the gate had widened to admit her. */}
          {posture.fromCascade && (
            <p
              data-testid="hero-source-note"
              className="mt-2 font-mono text-[10.5px] uppercase tracking-[.12em] text-sky-label"
            >
              {t("home.hero.fromCascade")}
            </p>
          )}
        </div>
        <PostureGauge impacted={impacted} total={total} rag={rag} sar={sar} />
      </div>
      <HorizonStrip
        entities={impacted}
        nodes={nodes}
        mvbImpacted={posture.mvbImpacted}
        mvbTotal={posture.mvbTotal}
        sev={sev}
      />
    </div>
  );
}
